import React from 'react';
import {
    DmsTextField,
    useGet,
    intl,
    fetchData,
    HTTP_STATUS
} from 'dmsbasereact';
import { SettingsViewEdit } from '../../components/SettingsLayouts';
import { Grid, Typography, Box, Tooltip, IconButton, Divider } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

/**
 * ApplicationSettings - General application configuration.
 * Uses view/edit mode pattern for consistent UX.
 */

/**
 * ViewContent - Display application settings in read-only mode
 */
const ViewContent = ({ data }) => {
    return (
        <Box>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {intl.get('DispenserSettings').d('Dispenser Settings')}
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Box flex={1}>
                            <Typography variant="caption" color="text.secondary">
                                {intl.get('DispenserDateChangeRecyclingLabel').d(
                                    'Dispensation Date Change Limit'
                                )}
                            </Typography>
                            <Typography variant="body1">
                                {data.dispenser_changing_dispensation_date_recycling}
                            </Typography>
                        </Box>
                        <Tooltip
                            title={intl.get('DispenserDateChangeRecyclingDescription').d(
                                'How many times a dispenser can change the dispensation date before having to watch the recycling training?'
                            )}
                            arrow
                        >
                            <IconButton size="small">
                                <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {intl.get('BatchMovementSettings').d('Stock Movement Settings')}
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Box flex={1}>
                            <Typography variant="caption" color="text.secondary">
                                {intl.get('BatchMovementExpirationBlockDaysLabel').d(
                                    'Expiration Block Window (days)'
                                )}
                            </Typography>
                            <Typography variant="body1">
                                {data.batch_movement_expiration_block_days === 0
                                    ? intl.get('BatchMovementSettings.Disabled').d('Disabled')
                                    : data.batch_movement_expiration_block_days}
                            </Typography>
                        </Box>
                        <Tooltip
                            title={intl.get('BatchMovementExpirationBlockDaysDescription').d(
                                'Batches expiring within this many days will be blocked from checkout and check-in (manual check-in). Set to 0 to disable the rule.'
                            )}
                            arrow
                        >
                            <IconButton size="small">
                                <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

/**
 * EditContent - Display application settings as editable form fields
 */
const EditContent = ({ formData, onFieldChange }) => {
    return (
        <Box>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {intl.get('DispenserSettings').d('Dispenser Settings')}
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <DmsTextField
                        name="dispenser_changing_dispensation_date_recycling"
                        label={intl.get('DispenserDateChangeRecyclingLabel').d(
                            'Dispensation Date Change Limit'
                        )}
                        type="number"
                        fullWidth
                        value={formData.dispenser_changing_dispensation_date_recycling ?? 3}
                        onChange={(e) => onFieldChange('dispenser_changing_dispensation_date_recycling', e.target.value)}
                        helperText={intl.get('DispenserDateChangeRecyclingDescription').d(
                            'How many times a dispenser can change the dispensation date before having to watch the recycling training? (0 = unlimited)'
                        )}
                        inputProps={{ min: 0 }}
                    />
                </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {intl.get('BatchMovementSettings').d('Stock Movement Settings')}
            </Typography>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <DmsTextField
                        name="batch_movement_expiration_block_days"
                        label={intl.get('BatchMovementExpirationBlockDaysLabel').d(
                            'Expiration Block Window (days)'
                        )}
                        type="number"
                        fullWidth
                        value={formData.batch_movement_expiration_block_days ?? 180}
                        onChange={(e) => onFieldChange('batch_movement_expiration_block_days', e.target.value)}
                        helperText={intl.get('BatchMovementExpirationBlockDaysDescription').d(
                            'Batches expiring within this many days will be blocked from checkout and check-in (manual check-in). Set to 0 to disable the rule.'
                        )}
                        inputProps={{ min: 0 }}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export const ApplicationSettings = () => {
    const { data, isLoading, refetch } = useGet('/application-setting', null, true);
    const { data: batchMovementData, isLoading: isBatchMovementLoading, refetch: refetchBatchMovement } = useGet('/batch_movement_settings', null, true);

    const mergedData = {
        ...(data?.data ?? {}),
        ...(batchMovementData?.data ?? {}),
    };

    const handleSave = async (payload) => {
        try {
            const appPayload = {
                dispenser_changing_dispensation_date_recycling: parseInt(payload.dispenser_changing_dispensation_date_recycling, 10)
            };
            const batchMovementPayload = {
                batch_movement_expiration_block_days: parseInt(payload.batch_movement_expiration_block_days, 10)
            };

            const [appResponse, batchResponse] = await Promise.all([
                fetchData('/application-setting', 'PUT', true, appPayload, false, true),
                fetchData('/batch_movement_settings', 'PUT', true, batchMovementPayload, false, true),
            ]);

            if (appResponse.status === HTTP_STATUS.OK_200 && batchResponse.status === HTTP_STATUS.OK_200) {
                return { ...appResponse.data.data, ...batchResponse.data.data };
            }
            return null;
        } catch (error) {
            console.error('Error saving application settings:', error);
            return null;
        }
    };

    const handleRefetch = () => {
        refetch();
        refetchBatchMovement();
    };

    return (
        <SettingsViewEdit
            data={mergedData}
            loading={isLoading || isBatchMovementLoading}
            title={intl.get('ApplicationSettings').d('Application Settings')}
            description={intl.get('ApplicationSettingsDescription').d(
                'Configure general application settings and behavior.'
            )}
            viewContent={ViewContent}
            editContent={EditContent}
            prepareFormData={(data) => ({
                dispenser_changing_dispensation_date_recycling: data.dispenser_changing_dispensation_date_recycling ?? 3,
                batch_movement_expiration_block_days: data.batch_movement_expiration_block_days ?? 180,
            })}
            buildPayload={(formData) => ({
                dispenser_changing_dispensation_date_recycling: formData.dispenser_changing_dispensation_date_recycling,
                batch_movement_expiration_block_days: formData.batch_movement_expiration_block_days,
            })}
            onSave={handleSave}
            onDataUpdated={handleRefetch}
            confirmMessage={intl.get('ConfirmSaveApplicationMessage').d(
                'Are you sure you want to save the application settings changes?'
            )}
        />
    );
};
